
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFileUpload } from '@/pages/workflow/hooks/useFileUpload';
import { Trash2, Plus, Link, Palette, CreditCard, Loader2, Image, Upload, X } from 'lucide-react';

const colors = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
];

interface NodeDescriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string;
  nodeLabel: string;
  nodeDescription: string;
  nodeColor: string;
  nodeImageUrl?: string;
  isRoot?: boolean;
  boardId: string;
  onSave: (label: string, description: string, imageUrl?: string) => void;
  onDelete: () => void;
  onColorChange: (color: string) => void;
  onAddChild: () => void;
  onConvertToCard: (columnId: string) => void;
  onEdgeDelete: (edgeId: string) => void;
}

export default function NodeDescriptionDialog({
  open,
  onOpenChange,
  nodeId,
  nodeLabel,
  nodeDescription,
  nodeColor,
  nodeImageUrl = '',
  isRoot = false,
  boardId,
  onSave,
  onDelete,
  onColorChange,
  onAddChild,
  onConvertToCard,
  onEdgeDelete
}: NodeDescriptionDialogProps) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [currentColor, setCurrentColor] = useState('');
  const [showConvertOptions, setShowConvertOptions] = useState(false);
  const [selectedColumnId, setSelectedColumnId] = useState<string>('');
  const [isConverting, setIsConverting] = useState(false);

  const { uploadFile, isUploading } = useFileUpload('mindmap-images');

  // Buscar colunas do quadro
  const { data: columns, isLoading: isLoadingColumns } = useQuery({
    queryKey: ['board-columns', boardId],
    queryFn: async () => {
      if (!boardId) return [];
      
      const { data, error } = await supabase
        .from('kanban_columns')
        .select('*')
        .eq('board_id', boardId)
        .order('position', { ascending: true });
      
      if (error) throw error;
      return data;
    },
    enabled: open && !!boardId
  });

  // Sincronizar props com estado local quando o dialog abrir
  useEffect(() => {
    if (open) {
      setLabel(nodeLabel || '');
      setDescription(nodeDescription || '');
      setImageUrl(nodeImageUrl || '');
      setCurrentColor(nodeColor || '#3B82F6');
      setSelectedColumnId('');
      setShowConvertOptions(false);
    }
  }, [open, nodeLabel, nodeDescription, nodeImageUrl, nodeColor]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = await uploadFile(file, `mindmap-${boardId}/node-${nodeId}`);
      if (result.data) {
        setImageUrl(result.data.url);
        console.log('Image uploaded successfully:', result.data.url);
      } else if (result.error) {
        console.error('Upload error:', result.error);
        throw result.error;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleRemoveImage = () => {
    setImageUrl('');
  };

  const handleSave = () => {
    const trimmedLabel = label.trim() || 'Nova Ideia';
    onSave(trimmedLabel, description, imageUrl);
    
    if (currentColor !== nodeColor) {
      onColorChange(currentColor);
    }
  };

  const handleDelete = () => {
    onDelete();
  };

  const handleColorSelect = (color: string) => {
    setCurrentColor(color);
  };

  const handleAddChild = () => {
    onAddChild();
  };

  const handleConvertToCard = async () => {
    if (!selectedColumnId) return;
    
    setIsConverting(true);
    
    try {
      onConvertToCard(selectedColumnId);
      setSelectedColumnId('');
      setShowConvertOptions(false);
    } catch (error) {
      console.error('Error converting node to card:', error);
    } finally {
      setIsConverting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Ideia</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Título</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Digite o título da ideia"
              autoFocus
            />
          </div>

          {/* Upload de Imagem */}
          <div>
            <label className="text-sm font-medium mb-2 block">Imagem</label>
            {imageUrl ? (
              <div className="relative">
                <img
                  src={imageUrl}
                  alt="Node image"
                  className="w-full max-w-xs h-32 object-cover rounded-md border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1"
                  onClick={handleRemoveImage}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-2">
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Clique para fazer upload de uma imagem
                      </span>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                      />
                    </label>
                  </div>
                  {isUploading && (
                    <div className="mt-2">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      <p className="text-sm text-gray-500 mt-1">Fazendo upload...</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Descrição</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Adicione uma descrição detalhada para esta ideia..."
              rows={4}
            />
          </div>

          {/* Seletor de Cor */}
          <div>
            <label className="text-sm font-medium mb-2 block">Cor</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  type="button"
                >
                  <Palette className="w-4 h-4 mr-2" />
                  <div 
                    className="w-4 h-4 rounded mr-2"
                    style={{ backgroundColor: currentColor }}
                  />
                  Alterar cor
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2">
                <div className="grid grid-cols-5 gap-1">
                  {colors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="w-8 h-8 rounded border-2 border-gray-200 hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      onClick={() => handleColorSelect(color)}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Converter para Cartão */}
          <div>
            <label className="text-sm font-medium mb-2 block">Converter para Cartão</label>
            {!showConvertOptions ? (
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setShowConvertOptions(true)}
                type="button"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Converter em cartão do fluxo
              </Button>
            ) : (
              <div className="space-y-2">
                {isLoadingColumns ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : (
                  <Select value={selectedColumnId} onValueChange={setSelectedColumnId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Escolha uma coluna" />
                    </SelectTrigger>
                    <SelectContent>
                      {columns?.map((column) => (
                        <SelectItem key={column.id} value={column.id}>
                          {column.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowConvertOptions(false)}
                    type="button"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleConvertToCard} 
                    disabled={!selectedColumnId || isConverting}
                    type="button"
                  >
                    {isConverting ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        Convertendo...
                      </>
                    ) : (
                      'Converter'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Adicionar Ideia Conectada */}
          <div>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleAddChild}
              type="button"
            >
              <Link className="w-4 h-4 mr-2" />
              Adicionar ideia conectada
            </Button>
          </div>
        </div>
        
        <div className="flex justify-between pt-4">
          <div>
            {!isRoot && (
              <Button 
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                type="button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              type="button"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              type="button"
            >
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
